'use client'

import { useState } from 'react'
import { User } from '@/types'
import { getUserByEmail } from '@/lib/emailValidation'


interface EmailManagementProps {
  // Add any props if needed
}

export default function EmailManagement({}: EmailManagementProps) {
  const [email, setEmail] = useState('')
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleEmailLookup = async () => {
    if (!email.trim()) {
      setError('Please enter an email address')
      return
    }

    setLoading(true)
    setError('')
    setUser(null)

    try {
      const userData = await getUserByEmail(email.trim())
      setUser(userData)
      
      if (!userData) {
        setError('No account found with this email address')
      }
    } catch (err) {
      setError('Failed to lookup user. Please try again.')
      console.error('Email lookup error:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleEmailLookup()
    }
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">
        Email Account Lookup
      </h3>
      
      <div className="space-y-4">
        {/* Email Input */}
        <div>
          <label htmlFor="email-lookup" className="block text-sm font-medium text-gray-700 mb-2">
            Email Address
          </label>
          <div className="flex gap-2">
            <input
              type="email"
              id="email-lookup"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyPress={handleKeyPress}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter email to lookup"
            />
            <button
              onClick={handleEmailLookup}
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Looking up...' : 'Lookup'}
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="p-3 bg-red-100 border border-red-300 rounded-md">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* User Information */}
        {user && (
          <div className="p-4 bg-green-50 border border-green-200 rounded-md">
            <h4 className="font-medium text-green-900 mb-3">Account Found</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Email:</span>
                <span className="font-medium">{user.email}</span>
              </div>
              {(user.firstName != null || user.lastName != null) && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Name:</span>
                  <span className="font-medium">
                    {[user.firstName, user.lastName].filter(Boolean).join(' ') || '—'}
                  </span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-600">Role:</span>
                <span className="font-medium capitalize">{user.role}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Verified:</span>
                <span className={`font-medium ${user.is_verified ? 'text-green-600' : 'text-red-600'}`}>
                  {user.is_verified ? 'Yes' : 'No'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Created:</span>
                <span className="font-medium">
                  {user.created_at
                    ? new Date(user.created_at).toLocaleDateString()
                    : '—'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">User ID:</span>
                <span className="font-mono text-xs">{user.id || '—'}</span>
              </div>
            </div>
          </div>
        )}

        {/* Help Text */}
        <div className="text-xs text-gray-500 mt-4">
          <p>This tool helps identify accounts associated with specific email addresses.</p>
          <p>Useful for resolving duplicate account issues or user support requests.</p>
        </div>
      </div>
    </div>
  )
} 


